<agr-print-page [showHeaderImage]="false" [showPageNumber]="false">
  <article class="print-report" data-test="print-report">
    <header class="report-header">
      <div>
        <p class="report-kicker">{{ 'dairyYieldScan.summary.print.title' | translate }}</p>
        <h1>
          {{
            (summary.baselinePotential === 0
              ? 'dairyYieldScan.summary.hero.reachedPotential'
              : 'dairyYieldScan.summary.hero.title'
            ) | translate
          }}
        </h1>
      </div>
      <div class="total-potential">
        <span>{{ 'dairyYieldScan.summary.hero.totalLabel' | translate }}</span>
        <strong>{{ summary.baselinePotential | number: '1.1-1' }} kg</strong>
        <small>{{ 'dairyYieldScan.summary.hero.totalUnit' | translate }}</small>
      </div>
    </header>

    <section class="report-section category-section" aria-labelledby="print-categories-heading">
      <h2 id="print-categories-heading">
        {{ 'dairyYieldScan.summary.categories.title' | translate }}
      </h2>
      <app-dairy-yield-scan-category-potentials [categoryPotentials]="summary.categoryPotentials" />
    </section>

    <app-dairy-yield-scan-insights
      class="report-section"
      [compliments]="summary.compliments"
      [improvements]="summary.improvements"
      [showComplimentOption]="false"
    />

    <section class="report-section figures-section" aria-labelledby="print-figures-heading">
      <header class="figures-header">
        <h2 id="print-figures-heading">{{ 'dairyYieldScan.summary.figures.title' | translate }}</h2>
        <p class="milk-price">
          {{ 'dairyYieldScan.summary.figures.milkPrice' | translate }}:
          @if (milkPrice !== null && milkPrice >= 0) {
            <strong>€ {{ milkPrice | number: '1.2-2' }} / 100 kg</strong>
          } @else {
            <strong>{{ 'dairyYieldScan.summary.figures.unavailable' | translate }}</strong>
          }
        </p>
      </header>
      <dl class="figures-list">
        <div>
          <dt>{{ 'dairyYieldScan.summary.figures.potentialMilk' | translate }}</dt>
          <dd>
            @if (annualPotentialMilk() !== null) {
              {{ annualPotentialMilk() | number: '1.0-0' }} kg
            } @else {
              {{ 'dairyYieldScan.summary.figures.unavailable' | translate }}
            }
          </dd>
        </div>
        <div>
          <dt>{{ 'dairyYieldScan.summary.figures.targetMilk' | translate }}</dt>
          <dd>
            @if (annualTargetMilk() !== null) {
              {{ annualTargetMilk() | number: '1.0-0' }} kg
            } @else {
              {{ 'dairyYieldScan.summary.figures.unavailable' | translate }}
            }
          </dd>
        </div>
        <div>
          <dt>{{ 'dairyYieldScan.summary.figures.potentialRevenue' | translate }}</dt>
          <dd>
            @if (annualPotentialRevenue() !== null) {
              € {{ annualPotentialRevenue() | number: '1.0-0' }}
            } @else {
              {{ 'dairyYieldScan.summary.figures.unavailable' | translate }}
            }
          </dd>
        </div>
        <div>
          <dt>{{ 'dairyYieldScan.summary.figures.targetRevenue' | translate }}</dt>
          <dd>
            @if (annualTargetRevenue() !== null) {
              € {{ annualTargetRevenue() | number: '1.0-0' }}
            } @else {
              {{ 'dairyYieldScan.summary.figures.unavailable' | translate }}
            }
          </dd>
        </div>
      </dl>
      <aside class="financial-warning">
        <mat-icon aria-hidden="true">warning_amber</mat-icon>
        <p>{{ 'dairyYieldScan.summary.warning.message' | translate }}</p>
      </aside>
    </section>

    <section class="report-section action-plan" aria-labelledby="print-actions-heading">
      <p class="report-kicker">{{ 'dairyYieldScan.summary.actions.eyebrow' | translate }}</p>
      <h2 id="print-actions-heading">{{ 'dairyYieldScan.summary.actions.title' | translate }}</h2>
      <section aria-labelledby="print-priorities-heading">
        <h3 id="print-priorities-heading">
          {{ 'dairyYieldScan.summary.actions.print.priorities' | translate }}
        </h3>
        @if (actionPlan()?.priorities?.length) {
          <ol class="priority-list">
            @for (priority of actionPlan()?.priorities; track priority) {
              <li>{{ priorityLabel(priority) | translate }}</li>
            }
          </ol>
        } @else {
          <p>{{ 'dairyYieldScan.summary.actions.print.notSpecified' | translate }}</p>
        }
      </section>

      <section class="impact-summary" aria-labelledby="print-impact-summary-heading">
        <header class="impact-summary-heading">
          <h3 id="print-impact-summary-heading">Wat leveren jullie acties op?</h3>
          @if (hasMilkYieldData()) {
            <div class="impact-target">
              <span>Doelstelling</span>
              <strong>{{ targetMilkYield | number: '1.1-1' }} kg meetmelk</strong>
              @if (remainingToTarget() !== null) {
                <small>
                  Nog {{ remainingToTarget() | number: '1.1-1' }} kg tot je doelstelling
                </small>
              }
            </div>
          }
        </header>

        @if (hasMilkYieldData()) {
          <div class="impact-equation">
            <article>
              <small>Huidige productie</small>
              <strong>{{ currentMilkYield | number: '1.1-1' }}</strong>
              <span>kg meetmelk</span>
            </article>
            <div class="equation-symbol" aria-hidden="true">+</div>
            <article class="action-value">
              <small>Gekozen acties</small>
              <strong>+{{ selectedActionPotential() | number: '1.1-1' }}</strong>
              <span>kg meetmelk</span>
            </article>
            <div class="equation-symbol" aria-hidden="true">=</div>
            <article class="improved-value">
              <small>Nieuwe te verwachten productie</small>
              <strong>{{ improvedMilkProduction() | number: '1.1-1' }}</strong>
              <span>kg meetmelk</span>
            </article>
          </div>
        } @else {
          <p class="impact-unavailable">De meetmelkproductie is niet beschikbaar.</p>
        }
      </section>

      <section class="agreements" aria-labelledby="print-agreements-heading">
        <h3 id="print-agreements-heading">
          {{ 'dairyYieldScan.summary.actions.agreementTitle' | translate }}
        </h3>
        <div class="agreement-field">
          <h4>{{ 'dairyYieldScan.summary.actions.farmerAction' | translate }}</h4>
          <p>
            {{
              actionPlan()?.farmerAction ||
                ('dairyYieldScan.summary.actions.print.notSpecified' | translate)
            }}
          </p>
        </div>
        <div class="agreement-field">
          <h4>{{ 'dairyYieldScan.summary.actions.advisorAction' | translate }}</h4>
          <p>
            {{
              actionPlan()?.advisorAction ||
                ('dairyYieldScan.summary.actions.print.notSpecified' | translate)
            }}
          </p>
        </div>
        <div class="agreement-field">
          <h4>{{ 'dairyYieldScan.summary.actions.evaluationDate' | translate }}</h4>
          @if (actionPlan()?.evaluationDate; as evaluationDate) {
            <p>{{ evaluationDate | agrDate }}</p>
          } @else {
            <p>{{ 'dairyYieldScan.summary.actions.print.notSpecified' | translate }}</p>
          }
        </div>
      </section>
    </section>
  </article>
</agr-print-page>
